import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import {InputField} from '@Components/input-field-v2';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {InputFieldControls, InputFieldHandlers, InputFieldNumberProperties} from '@Components/input-field-v2/input-field.types';
import {InputFieldSize, InputFieldState, InputFieldType} from '@Components/input-field-v2/input-field.types';
import styles from './input-counter.module.scss';
import useLongPress from '@/hooks/useLongPress';

interface InputCounterProps extends InputCounterControlHandlers, InputFieldHandlers, InputFieldNumberProperties {
  id?: string;
  className?: string;
  value: number;
  /**
   * length should be 1 or 2 at all times
   */
  controlIcons?: string[];
  controlsDisabledStates?: boolean[];
  size?: InputFieldSize;
  elementTitle?: string;
  isDisabled?: boolean;
  isInputFieldDisabled?: boolean;
  minimumStepFromInputField?: number;
  state?: InputFieldState;
  inputFieldControls?: InputFieldControls;
  inputFieldClassname?: string;

  /**
   * this automatically fires the on change and on change ended if value is changed from the controls
   * you can send this prop as false if you want to handle increment decrement yourself
   */
  fireOnChangeFromControls?: boolean;
  isValueBlank?: boolean;

  /**
   * in case someone wants to use their own set of controls
   */
  overrideDefaultContols?: boolean;
  onClickControl?(controlIndex: number): void;
}

export interface InputCounterControlHandlers {
  onDecrement?(): void;

  onIncrement?(): void;
}

export function InputCounter({
  id = '',
  className = '',
  controlIcons = ['icon-minus', 'icon-plus'],
  controlsDisabledStates = [false, false],
  size = InputFieldSize.SMALL,
  isDisabled = false,
  isInputFieldDisabled = false,
  onDecrement = (): void => {},
  onIncrement = (): void => {},
  onArrowUpKeyDown = (): void => {},
  onArrowDownKeyDown = (): void => {},
  onInputBlur = (): void => {},
  onInputSubmit = (): void => {},
  onInputFocus = (): void => {},
  onInputChange = (): void => {},
  onInputChangeEnded = (): void => {},
  state = InputFieldState.DEFAULT,
  min = 0,
  max = 100,
  step = 1,
  minimumStepFromInputField = step,
  fireOnChangeFromControls = true,
  isValueBlank = false,
  overrideDefaultContols = false,
  onClickControl = (): void => {},
  ...props
}: InputCounterProps): ReactElement<any> | null {
  const inputFieldRef = useRef<HTMLInputElement>(null);
  const incrementContainerRef = useRef<HTMLDivElement | null>(null);
  const decrementContainerRef = useRef<HTMLDivElement>(null);
  const onIncrementDecrementTimer = useRef<number | undefined>(undefined);

  const getSizeClassesForControls = (): string => {
    switch (size) {
      case InputFieldSize.XSMALL:
        return styles.xsmallControl;
      case InputFieldSize.SMALL:
        return styles.smallControl;
      case InputFieldSize.MEDIUM:
        return styles.mediumControl;
      default:
        return styles.smallControl;
    }
  };

  const getContainerSizeClasses = (): string => {
    switch (size) {
      case InputFieldSize.XSMALL:
        return styles.xsmallContainer;
      case InputFieldSize.SMALL:
        return styles.smallContainer;
      case InputFieldSize.MEDIUM:
        return styles.mediumContainer;
      default:
        return styles.smallContainer;
    }
  };

  const getIconSizes = (): IconSize => {
    switch (size) {
      case InputFieldSize.XSMALL:
      case InputFieldSize.SMALL:
        return IconSize.SIZE_ICON_16;
      case InputFieldSize.MEDIUM:
        return IconSize.SIZE_ICON_20;
      default:
        return IconSize.SIZE_ICON_16;
    }
  };

  const onFocusHandler = (value: number | string): void => {
    onInputFocus(value);
    inputFieldRef.current?.select();
  };

  const handleIncrementDecrement = (value: number): void => {
    if (!fireOnChangeFromControls) {
      return;
    }
    onInputChange(value);

    if (!onInputChangeEnded) {
      return;
    }
    if (onIncrementDecrementTimer) {
      clearTimeout(onIncrementDecrementTimer.current);
    }

    onIncrementDecrementTimer.current = window.setTimeout(() => {
      if (onInputChangeEnded) {
        onInputChangeEnded(value);
      }
    }, 600);
  };

  const onIncrementHandler = (): void => {
    if (isDisabled) {
      return;
    }

    if (overrideDefaultContols) {
      if (props.value + step > max) {
        return;
      }
      handleIncrementDecrement(props.value + step);
      return;
    }

    if (controlsDisabledStates[1]) {
      return;
    }

    if (incrementContainerRef && incrementContainerRef.current) {
      handleIncrementDecrement(props.value + step);
      incrementContainerRef.current?.classList.add(styles.addClickEffect);
      window.setTimeout(() => {
        incrementContainerRef.current?.classList.remove(styles.addClickEffect);
      }, 50);
    }
  };

  const onDecrementHandler = (): void => {
    if (isDisabled) {
      return;
    }

    if (overrideDefaultContols) {
      if (props.value - step < min) {
        return;
      }
      handleIncrementDecrement(props.value - step);
      return;
    }

    if (controlsDisabledStates[0]) {
      return;
    }

    if (decrementContainerRef && decrementContainerRef.current) {
      handleIncrementDecrement(props.value - step);
      decrementContainerRef.current?.classList.add(styles.addClickEffect);
      window.setTimeout(() => {
        decrementContainerRef.current?.classList.remove(styles.addClickEffect);
      }, 50);
    }
  };

  const preIncrementCallback = (): void => {
    if (overrideDefaultContols) {
      return;
    }
    if (isDisabled || controlsDisabledStates[1]) {
      return;
    }
    onIncrement();
    handleIncrementDecrement(props.value + step);
  };

  const preDecrementCallback = (): void => {
    if (overrideDefaultContols) {
      return;
    }
    if (isDisabled || controlsDisabledStates[0]) {
      return;
    }
    onDecrement();
    handleIncrementDecrement(props.value - step);
  };

  const incrementLongPress = useLongPress(preIncrementCallback, 75);
  const decrementLongPress = useLongPress(preDecrementCallback, 75);

  const getContainerClassesForStates = (): string => {
    if (isDisabled) {
      return styles.disabled;
    }

    switch (state) {
      case InputFieldState.SUCCESS:
        return styles.success;
      case InputFieldState.ERROR:
        return styles.error;
      case InputFieldState.DEFAULT:
      default:
        return '';
    }
  };

  const hasSingleControl = (): boolean => {
    return controlIcons?.length === 1;
  };

  return (
    <div id={id} className={`${className} ${styles.container} flex-row-align-center ${getContainerClassesForStates()} ${getContainerSizeClasses()}`}>
      <div
        {...decrementLongPress}
        ref={decrementContainerRef}
        onClick={(): void => {
          if (overrideDefaultContols) {
            onClickControl(1);
          }
          preDecrementCallback();
        }}
        className={`${styles.iconContainer} flex-center ${isDisabled ? styles.disabledBorderRight : styles.borderRight} ${
          isDisabled || controlsDisabledStates[0] ? styles.disabledControl : ''
        } ${styles.leftControl} ${getSizeClassesForControls()}`}
      >
        <Icon icon={controlIcons[0]} size={getIconSizes()} />
      </div>
      <InputField
        ref={inputFieldRef}
        elementTitle={props.elementTitle}
        className={`flex-1 ${styles.inputContainer}`}
        isDisabled={isDisabled || isInputFieldDisabled}
        inputClassName={`_borderless ${styles.input} ${hasSingleControl() ? styles.singleControlInput : ''} ${props.inputFieldClassname}`}
        onInputChange={(value: number): void => {
          return onInputChange(value);
        }}
        onInputChangeEnded={(value: number): void => {
          if (onInputChangeEnded) {
            onInputChangeEnded(value);
          }
        }}
        size={size}
        input={isValueBlank ? '' : props.value}
        inputType={InputFieldType.NUMBER}
        onInputSubmit={onInputSubmit}
        onInputBlur={onInputBlur}
        onInputFocus={onFocusHandler}
        onArrowUpKeyDown={(): void => {
          onIncrementHandler();
          onArrowUpKeyDown();
          if (!fireOnChangeFromControls) {
            onIncrement();
          }
        }}
        onArrowDownKeyDown={(): void => {
          onDecrementHandler();
          onArrowDownKeyDown();
          if (!fireOnChangeFromControls) {
            onDecrement();
          }
        }}
        min={min}
        max={max}
        step={minimumStepFromInputField}
        isMultiline={false}
        isValueBlank={isValueBlank}
        controls={props.inputFieldControls}
      />
      {!hasSingleControl() ? (
        <div
          {...incrementLongPress}
          ref={incrementContainerRef}
          onClick={(): void => {
            if (overrideDefaultContols) {
              onClickControl(1);
            }
            preIncrementCallback();
          }}
          className={`${styles.iconContainer} flex-center ${isDisabled ? styles.disabledBorderLeft : styles.borderLeft}  ${
            isDisabled || controlsDisabledStates[1] ? styles.disabledControl : ''
          } ${styles.rightControl} ${getSizeClassesForControls()}`}
        >
          <Icon icon={controlIcons[1]} size={getIconSizes()} />
        </div>
      ) : null}
    </div>
  );
}
